import { computed, observable } from 'mobx';

import { makePaginated, Paginated } from '../../adl-gen/common';
import { DbKey, WithDbId } from '../../adl-gen/common/db';
import { makeTableQuery, TableQuery } from '../../adl-gen/common/tabular';
import {
  AppClient,
  Question,
  Quiz,
  UserQuiz,
  WorkRole
} from '../../adl-gen/totalinteraction/futurefit/db';
import { Entry, RepositoryEntries, RepositoryEntrySet } from '../../adl-gen/totalinteraction/futurefit/frontend';
import {
  CreateQuizReq,
  makeRepositoryPutReq,
  makeRepositoryReq,
  RepositoryPutEntry,
  RepositoryPutReq,
  RepositoryReq
} from '../../adl-gen/totalinteraction/futurefit/requests';
import { fromPromise, MaybeLoading } from '../../loader/loader';
import { Service } from '../../service/service';
import { LoggedIn, LoggedInSuper } from '../../stores/identity-store';

import { DataStore, TransportLayer } from './datastore';

const TIMEOUT_MILLIS = 10;
/** A fake callback, to be used for unimplemented standard transport layer APIs */
// tslint:disable-next-line:no-any
export function fakeCallback(msg: string): any {
  return function() {
    // tslint:disable-next-line:no-console
    console.debug(msg, Array.from(arguments));
    return new Promise<void>(resolve => {
      setTimeout(resolve, TIMEOUT_MILLIS);
    });
  };
}

class QuizAdminApi implements TransportLayer<Quiz> {
  kind: string = 'Quiz';
  constructor(protected readonly service: Service) {}
  create = async (item: Quiz): Promise<DbKey<Quiz>> => fakeCallback(item.title.value.en); // this.service.createQuiz(item)
  update = async (itemWithId: WithDbId<Quiz>): Promise<void> => fakeCallback(itemWithId.id); // this.service.updateQuiz(itemWithId)
  doDelete = async (id: DbKey<Quiz>): Promise<void> => fakeCallback(id); // this.service.deleteQuiz(id)
  query = async (q: TableQuery): Promise<Paginated<WithDbId<Quiz>>> => this.service.queryQuizzes(q);

  createQuiz = async (item: CreateQuizReq): Promise<DbKey<Quiz>> => this.service.createQuiz(item);
  queryWorkRoles = async (q: TableQuery): Promise<Paginated<WithDbId<WorkRole>>> => this.service.queryWorkRoles(q);
  queryUserQuizzes = async (q: TableQuery): Promise<Paginated<WithDbId<UserQuiz>>> => this.service.queryUserQuizs(q);
  uploadRepositoryEntry = async (req: RepositoryPutReq): Promise<Entry> => this.service.uploadRepositoryEntry(req);
  listRepositoryEntries = async (req: RepositoryReq): Promise<RepositoryEntries> =>
    this.service.listRepositoryEntries(req);
  queryQuestions = async (q: TableQuery): Promise<Paginated<WithDbId<Question>>> => this.service.queryQuestions(q);
}

export class QuizAdminStore extends DataStore<Quiz> {
  @observable.ref clientId?: DbKey<AppClient>;
  @observable.ref workRole?: Promise<WithDbId<WorkRole>>;
  @observable.ref repository: Promise<RepositoryEntrySet>;

  /// id = WorkRole not Quiz
  public static fromService = (
    service: Service,
    identity: LoggedIn | LoggedInSuper,
    appClientId?: DbKey<AppClient>,
    workRoleId?: DbKey<WorkRole>,
    id?: DbKey<Quiz>
  ): QuizAdminStore => {
    const store = new QuizAdminStore(new QuizAdminApi(service), identity, appClientId, workRoleId, id);
    return store;
  };

  /// id = WorkRole not Quiz
  constructor(
    transport: TransportLayer<Quiz>,
    identity: LoggedIn | LoggedInSuper,
    appClientId?: DbKey<AppClient>,
    workRoleId?: DbKey<WorkRole>,
    id?: DbKey<Quiz>
  ) {
    super(transport, identity, id);
    if (appClientId) {
      this.clientId = appClientId;
    }
    if (workRoleId) {
      this.workRole = this.loadWorkRole(workRoleId);
    }
    // Don't autoload all data, we might only want a subset - hence not in base class
    this.data = this.init(workRoleId, id);
    this.repository = this.initRepository();
  }

  /// id = WorkRole not Quiz
  /** @override */
  init = async (workRoleId?: DbKey<WorkRole>, id?: DbKey<Quiz>): Promise<Paginated<WithDbId<Quiz>>> => {
    if (this.authorized) {
      if (workRoleId) {
        return this.loadFromWorkRole(workRoleId);
      } else if (id) {
        const results = await this.query(this.queryId(id));
        return results;
      } else {
        const results = await this.query(this.queryAll);
        return results;
      }
    } else {
      return Promise.reject('Not authorized');
    }
  };

  public createQuiz = async (item: CreateQuizReq): Promise<DbKey<Quiz>> => {
    if (this.authorized) {
      const quizId = await (this.transport as QuizAdminApi).createQuiz(item);
      return quizId;
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly loadWorkRole = async (roleId: DbKey<WorkRole>): Promise<WithDbId<WorkRole>> => {
    if (this.authorized) {
      const query = makeTableQuery({
        filter: {
          kind: 'equals',
          value: {
            field: 'id',
            value: roleId
          }
        }
      });
      const roles = await this.queryWorkRoles(query);
      if (roles.total_size !== 1) {
        return Promise.reject(`WorkRole not found for id ${roleId}`);
      }
      return roles.items[0];
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly loadFromWorkRole = async (role: DbKey<WorkRole>): Promise<Paginated<WithDbId<Quiz>>> => {
    if (this.authorized) {
      const query = makeTableQuery({
        filter: {
          kind: 'equals',
          value: {
            field: 'workRoleId',
            value: role
          }
        }
      });
      const quizzes = await this.query(query);
      return quizzes;
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly loadUserQuizzes = async (quizzes: WithDbId<Quiz>[]): Promise<Paginated<WithDbId<UserQuiz>>> => {
    if (quizzes.length > 0) {
      const query = makeTableQuery({
        filter: {
          kind: 'in',
          value: {
            field: 'quizId',
            values: [...quizzes.map(q => q.id)]
          }
        }
      });
      const userQuizzes = await (this.transport as QuizAdminApi).queryUserQuizzes(query);
      return userQuizzes;
    } else {
      return makePaginated({
        items: [],
        current_offset: 0,
        total_size: 0
      });
    }
  };

  private readonly loadNonSkillQuestions = async (): Promise<Paginated<WithDbId<Question>>> => {
    const query = makeTableQuery({});
    const allQuestions = await (this.transport as QuizAdminApi).queryQuestions(query);
    const filteredQuestions = allQuestions.items.filter(item => item.value.category.kind !== 'skill');
    return makePaginated({
      items: filteredQuestions,
      current_offset: 0,
      total_size: filteredQuestions.length
    });
  };

  @computed
  public get loadingNonSkillQuestions(): MaybeLoading<Paginated<WithDbId<Question>>> {
    if (this.authorized) {
      if (this.loading.kind === 'value') {
        return fromPromise(this.loadNonSkillQuestions());
      } else {
        return { kind: 'loading' };
      }
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  @computed
  public get loadingUserQuiz(): MaybeLoading<Paginated<WithDbId<UserQuiz>>> {
    if (this.authorized) {
      if (this.loading.kind === 'value') {
        return fromPromise(this.loadUserQuizzes(this.loading.value.items));
      } else {
        return { kind: 'loading' };
      }
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  @computed
  public get loadingWorkRole(): MaybeLoading<WithDbId<WorkRole>> {
    if (this.authorized) {
      if (this.workRole) {
        return fromPromise(this.workRole);
      } else {
        return { kind: 'loading' };
      }
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  private readonly queryWorkRoles = async (q: TableQuery): Promise<Paginated<WithDbId<WorkRole>>> => {
    const roles = await (this.transport as QuizAdminApi).queryWorkRoles(q);
    return roles;
  };

  // /// Override base method - and set async
  // @action
  // reload = () => {
  //     if (this.selectedKey) {
  //         this.data = this.load(this.selectedKey)
  //     }
  // }

  // @action
  // setRoleFilter = (r: WithDbId<WorkRole>) => {
  //     this.selectedKey = r
  //     // Don't call reload() here
  //     this.data = this.load(r)
  // }

  /// S3 Actions
  private readonly initRepository = async (): Promise<RepositoryEntrySet> => {
    const repository: RepositoryEntrySet = {};
    if (this.authorized) {
      return this.loadRepositories(repository);
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly loadRepositoryEntries = async (path: string): Promise<RepositoryEntrySet> => {
    return {
      [path]: await this.listRepositoryEntries(
        makeRepositoryReq({
          appClientId: this.identity.appClient.id,
          path
        })
      )
    };
  };

  private readonly loadRepositories = async (repository: RepositoryEntrySet): Promise<RepositoryEntrySet> => {
    try {
      const quizTileFolder = 'data-images/assessments/tiles';
      let repos = {};
      repos = await this.loadRepositoryEntries(quizTileFolder);
      return { ...repository, ...repos };
    } catch (e) {
      return Promise.reject(`Unable to load default repositories: ${(e as Error).message}`);
    }
  };

  private readonly listRepositoryEntries = async (req: RepositoryReq): Promise<RepositoryEntries> => {
    if (this.authorized) {
      return (this.transport as QuizAdminApi).listRepositoryEntries(req);
    } else {
      return Promise.reject('Not authorized');
    }
  };

  public uploadRepositoryEntry = async (req: RepositoryPutEntry): Promise<Entry> => {
    if (this.authorized) {
      return (this.transport as QuizAdminApi).uploadRepositoryEntry(
        makeRepositoryPutReq({
          appClientId: this.identity.appClient.id,
          entry: req
        })
      );
    } else {
      return Promise.reject('Not authorized');
    }
  };

  @computed
  public get loadingRepository(): MaybeLoading<RepositoryEntrySet> {
    if (this.authorized) {
      return fromPromise(this.repository);
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }
}



// WEBPACK FOOTER //
// ./src/admin/stores/quiz-admin-store.ts